import { FloatingLabel, Form, FormCheck } from 'react-bootstrap';
import { doNothing, find } from 'remeda';
import React, { createContext, useContext, useEffect } from 'react';
import { useAllAccounts } from '@/hooks/hooks';
import { useSession } from '@/api/auth/client';
import { AccountDTO } from '@repo/common/schemas/models';

export type GlobalAccountContext = {
  isActualInternalUser: boolean;
  simulateNonInternalUser: boolean;
  isInternalUser: boolean;
  isImpersonation: boolean;
  isAdmin: boolean;
  isClient: boolean;
  isLocked: boolean;
  setSimulateNonInternalUser: (val: boolean) => void;
  selectedAccount: AccountDTO | null;
  setSelectedAccount: (selectedAccount: AccountDTO | null) => void;
};
export const GlobalAccountContext = createContext<GlobalAccountContext>({
  isActualInternalUser: false,
  simulateNonInternalUser: false,
  isInternalUser: false,
  isImpersonation: false,
  isAdmin: false,
  isClient: true,
  isLocked: false,
  setSimulateNonInternalUser: doNothing,
  selectedAccount: null,
  setSelectedAccount: doNothing,
});
export function SimulateNonInternalUserToggle() {
  const { isActualInternalUser, simulateNonInternalUser, setSimulateNonInternalUser } =
    useContext(GlobalAccountContext);
  if (!isActualInternalUser) {
    return null;
  }
  return (
    <FormCheck
      type="switch"
      checked={simulateNonInternalUser}
      onChange={(e) => setSimulateNonInternalUser(e.target.checked)}
      label="Non-Admin Mode"
    />
  );
}

export const useGlobalAccountContext = () => useContext(GlobalAccountContext);

export function GlobalAccountPicker() {
  const { data: session } = useSession();
  const { simulateNonInternalUser, selectedAccount, setSelectedAccount } =
    useContext(GlobalAccountContext);

  if (!session?.user?.isInternal) {
    return null;
  }
  return (
    <AccountPicker
      disabled={!simulateNonInternalUser}
      account={selectedAccount}
      setAccount={setSelectedAccount}
      isGlobal
    />
  );
}

export function BullhornAccountPicker({
  account,
  setAccount,
}: {
  account: AccountDTO | null;
  setAccount: (value: AccountDTO | null) => void;
}) {
  const { bullhornAccounts } = useAllAccounts();
  return (
    <BaseAccountPicker account={account} setAccount={setAccount} accountList={bullhornAccounts} />
  );
}

export function AccountPicker({
  account,
  setAccount,
  disabled = false,
  isGlobal = false,
}: {
  account: AccountDTO | null;
  setAccount: (value: AccountDTO | null) => void;
  disabled?: boolean;
  isGlobal?: boolean;
}) {
  const { allAccounts } = useAllAccounts(isGlobal);

  useEffect(() => {
    if (allAccounts?.length === 1 && !account) {
      setAccount(allAccounts[0]);
    }
  }, [allAccounts, setAccount, account]);

  return (
    <BaseAccountPicker
      account={account}
      setAccount={setAccount}
      accountList={allAccounts}
      disabled={disabled}
    />
  );
}

function BaseAccountPicker<T extends AccountDTO = AccountDTO>({
  account,
  setAccount,
  accountList,
  disabled = false,
}: {
  account: T | null;
  setAccount: (value: T | null) => void;
  accountList: T[];
  disabled?: boolean;
}) {
  return (
    <>
      <FloatingLabel controlId="account-select" label="Account">
        <Form.Select
          className="form-control"
          id="account"
          value={account?._id || ''}
          disabled={disabled}
          onChange={(e) => {
            setAccount(find(accountList, ({ _id }) => _id === e.target.value) || null);
          }}
        >
          <option value={''}>Select an account</option>
          {accountList.map(({ _id, name }: { _id: string; name: string }) => (
            <option value={_id} key={_id}>
              {name}
            </option>
          ))}
        </Form.Select>
      </FloatingLabel>
    </>
  );
}
